/**
 * EmailDetailPage
 *
 * Shows one of the investor's own emails, including the full rendered body.
 */

import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DeliveryStatusBadge } from '@/components/email/DeliveryStatusBadge';
import { EmailAttachments } from '@/components/email/EmailAttachments';
import { formatDateTime, formatReferenceType } from '@/utils/formatters';
import { useEmailDetail } from '../hooks/useEmails';

export function EmailDetailPage() {
  const navigate = useNavigate();
  const { emailId } = useParams<{ emailId: string }>();
  const { data: email, isLoading, error } = useEmailDetail(emailId);

  const withBack = (children: ReactNode) => (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/portal/emails')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Emails
      </Button>
      {children}
    </div>
  );

  if (isLoading) {
    return withBack(
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !email) {
    return withBack(
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <h3 className="mb-2 text-lg font-medium text-foreground">
            Email Not Found
          </h3>
          <p>This email is not available.</p>
        </CardContent>
      </Card>
    );
  }

  return withBack(
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground">
              {email.subject}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {formatReferenceType(email.type)} · {formatDateTime(email.date)}
            </p>
          </div>
          <DeliveryStatusBadge
            status={email.delivery_status}
            className="flex-shrink-0"
          />
        </div>

        <div className="border-t pt-4">
          <p className="text-sm text-muted-foreground">To</p>
          <p className="font-medium text-foreground">{email.to_email}</p>
        </div>

        {email.attachments && email.attachments.length > 0 && (
          <div className="border-t pt-4">
            <p className="mb-3 text-sm text-muted-foreground">Attachments</p>
            <EmailAttachments attachments={email.attachments} />
          </div>
        )}

        <div className="border-t pt-4">
          <p className="mb-3 text-sm text-muted-foreground">Email content</p>
          {email.html_content ? (
            <div
              className="prose prose-sm max-w-none rounded-lg border bg-card p-6 dark:prose-invert"
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- email HTML is rendered from our own backend Jinja templates with autoescape
              dangerouslySetInnerHTML={{ __html: email.html_content }}
            />
          ) : (
            <p className="py-8 text-center text-muted-foreground">
              The rendered content for this email is not available.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
